import { backend, BackendQueryKeys, useTypeSafeQuery } from '@oresundsbron/api';
import { sortBy } from 'fp-ts/lib/Array';
import { pipe } from 'fp-ts/lib/function';
import { useRouter } from 'next/router';
import { OptgroupHTMLAttributes, useMemo } from 'react';
import {
  byCountryLocale,
  byPromotedCountries,
  byString,
  promotedCountries,
} from '../lib/sort';

type CountryCodeItem = {
  key: string;
  value: string;
  disabled?: boolean;
  label: string;
};

type CountryCodeDataItem = {
  key: string;
  value: string;
};

const sort = (locale: string) =>
  sortBy([
    byPromotedCountries<CountryCodeDataItem>((item) => item.value),
    byCountryLocale<CountryCodeDataItem>((item) => item.value)(locale),
    byString<CountryCodeDataItem>((item) => item.key),
  ]);

export function useCountries() {
  const { locale } = useRouter();
  const { data } = useTypeSafeQuery(
    BackendQueryKeys.CountryList({ locale }),
    (req) => req.Country.list(locale || ''),
    { staleTime: Infinity }
  );

  return useMemo(() => pipe(data || [], sort(locale || '')), [data, locale]);
}

const addLabel = (data: CountryCodeDataItem[]) =>
  data.map((d) => {
    return { ...d, label: d.key };
  });

export type CountryCodeOptItem = {
  group?: OptgroupHTMLAttributes<string>;
  children: CountryCodeItem[];
};

export function useCountriesOptGroups() {
  const { locale } = useRouter();
  const { data } = useTypeSafeQuery(
    BackendQueryKeys.CountryList({ locale }),
    (req) => req.Country.list(locale || ''),
    { staleTime: Infinity }
  );

  return useMemo(
    () =>
      pipe(data || [], sort(locale || ''), addLabel, (d) => {
        return [
          {
            children: [...d.splice(0, promotedCountries.length)].sort(
              (a, b) =>
                promotedCountries.indexOf(a.value) -
                promotedCountries.indexOf(b.value)
            ),
            group: { label: 'form.labels.popular' },
          },
          {
            children: [...d],
            group: { label: 'form.labels.countries' },
          },
        ];
      }),
    [data, locale]
  );
}
